// Ficha de una llamada: quien, cuando, agente, duracion y costo; la tipificacion y sus notas se pueden corregir
// (re-tipificar) aqui mismo, sin volver a abrir el cierre del telefono.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { analisisLlamada } from '../api/cdr';
import * as crm from '../api/crm';
import type { Llamada } from '../api/types';
import { useSession } from '../auth/session';
import { K, useAgentes, useTipificaciones } from '../data/queries';
import { esContestada } from '../data/selectors';
import { duration, money } from '../lib/format';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { phone } from '../phone/store';
import { ErrorBox, Pill, Skeleton } from './bits';
import { Dialog } from './Dialog';
import { abrirDialogo } from './forms';
import { toast } from './toasts';

export const sentidoLlamada = (l: Llamada) =>
  l.direccion === 'INTERNA' ? 'Interna' : !esContestada(l) ? (l.direccion === 'ENTRANTE' ? 'Perdida' : 'No contestada') : l.direccion === 'ENTRANTE' ? 'Entrante' : 'Saliente';

export function LlamadaFicha({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const r = useQuery({ queryKey: ['llamada', id], queryFn: () => crm.llamada(id) });
  const tips = useTipificaciones();
  const agentes = useAgentes();
  const { tieneModulo } = useSession();
  const [tipId, setTipId] = useState('');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!r.data) return;
    setTipId(r.data.tipificacionId ?? '');
    setNotas(r.data.notas);
  }, [r.data]);

  if (r.isPending)
    return (
      <Dialog titulo="Llamada" onClose={onClose} width={620}>
        <Skeleton rows={6} />
      </Dialog>
    );
  if (r.error)
    return (
      <Dialog titulo="Llamada" onClose={onClose} width={620}>
        <ErrorBox error={r.error} onRetry={() => r.refetch()} />
      </Dialog>
    );

  const l = r.data;
  const contestada = esContestada(l);
  const contacto = l.persona ? { id: l.persona.id, nombre: l.persona.nombre, empresa: l.empresa?.nombre } : null;
  const cuando = new Date(l.inicio ?? l.creado).toLocaleString('es-CO', { dateStyle: 'full', timeStyle: 'short' });
  const puedeTipificar = contestada && tieneModulo('TIPIFICACION');
  const cambio = tipId !== (l.tipificacionId ?? '') || notas !== l.notas;

  const guardar = async () => {
    if (!tipId) return toast('Elige una tipificación.', 'info');
    setGuardando(true);
    try {
      const res = await crm.tipificar(l.id, tipId, notas);
      void qc.invalidateQueries({ queryKey: ['llamada', id] });
      void qc.invalidateQueries({ queryKey: ['llamadas'] });
      if (res.tareaId) void qc.invalidateQueries({ queryKey: K.tareas });
      toast(res.tareaId ? 'Tipificación guardada. Se creó la tarea de seguimiento.' : 'Tipificación guardada.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog titulo={l.persona?.nombre || formatPhone(l.numero) || 'Llamada'} subtitulo={`${sentidoLlamada(l)} · ${cuando}`} onClose={onClose} width={620}>
      <div className="form">
        <dl className="kv">
          <dt>Número</dt>
          <dd className="mono">{l.numero ? formatPhone(l.numero) : '—'}</dd>
          <dt>Contacto</dt>
          <dd>
            {l.persona ? (
              <Link to={`/personas/${l.persona.id}`} onClick={() => abrirDialogo(null)}>
                {l.persona.nombre}
              </Link>
            ) : (
              'Sin ficha'
            )}
          </dd>
          {l.empresa ? (
            <>
              <dt>Empresa</dt>
              <dd>
                <Link to={`/empresas/${l.empresa.id}`} onClick={() => abrirDialogo(null)}>
                  {l.empresa.nombre}
                </Link>
              </dd>
            </>
          ) : null}
          <dt>Agente</dt>
          <dd>{agentes.conExt(l.agente)}</dd>
          <dt>Duración</dt>
          <dd className="mono">{l.duracion ? duration(l.duracion) : '—'}</dd>
          {l.costo ? (
            <>
              <dt>Costo</dt>
              <dd className="mono">
                {money(l.costo, l.moneda)}
                {l.destino ? <span className="sb"> · {l.destino}</span> : null}
              </dd>
            </>
          ) : null}
          <dt>Estado</dt>
          <dd>
            {l.tipificacion ? (
              <Pill tone={l.gestion === 'SEGUIMIENTO' ? 'warn' : 'acc'}>{l.tipificacion.nombre}</Pill>
            ) : contestada ? (
              <Pill tone="warn">Sin tipificar</Pill>
            ) : (
              <Pill tone="bad">{sentidoLlamada(l)}</Pill>
            )}
          </dd>
        </dl>

        <Analisis3cxLlamada inicio={l.inicio ?? l.creado} numero={l.numero} agente={l.agente} onUsarResumen={puedeTipificar ? (t) => setNotas((n) => (n ? `${n}

${t}` : t)) : undefined} />

        {puedeTipificar ? (
          <>
            <h3 className="lbl" style={{ margin: 0 }}>
              {l.tipificacion ? 'Corregir tipificación' : 'Tipificar'}
            </h3>
            <label>
              Resultado de la llamada
              <select id="f-ll-tip" value={tipId} onChange={(e) => setTipId(e.target.value)}>
                <option value="">Elige…</option>
                {(tips.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                    {t.requiereSeguimiento ? ' (crea seguimiento)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Notas
              <textarea id="f-ll-notas" value={notas} onChange={(e) => setNotas(e.target.value)} rows={3} placeholder="Lo que se habló, acuerdos…" />
            </label>
          </>
        ) : l.notas ? (
          <>
            <h3 className="lbl" style={{ margin: 0 }}>
              Notas
            </h3>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{l.notas}</p>
          </>
        ) : null}

        <div className="actions">
          <button className="btn ghost" onClick={onClose} data-close style={{ marginRight: 'auto' }}>
            Cerrar
          </button>
          <button
            className="btn"
            onClick={() => abrirDialogo({ tipo: 'tarea', personaId: l.personaId ?? undefined, empresaId: l.empresaId ?? undefined, nombre: l.persona?.nombre, volverA: { tipo: 'verLlamada', id } })}
          >
            <Icon name="task" /> Tarea
          </button>
          {l.numero ? (
            <button className="btn ok" onClick={() => phone.dial(l.numero, contacto)}>
              <Icon name="phone" /> {contestada ? 'Llamar' : 'Devolver'}
            </button>
          ) : null}
          {puedeTipificar ? (
            <button className="btn primary" onClick={guardar} disabled={guardando || !cambio || !tipId}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

// D80: lo que la IA de 3CX dijo de esta llamada (resumen, transcripcion y sentimiento), si la central lo tiene activo.
function Analisis3cxLlamada({ inicio, numero, agente, onUsarResumen }: { inicio: string; numero: string; agente: string; onUsarResumen?: (t: string) => void }) {
  const a = useQuery({ queryKey: ['analisis-3cx', inicio, numero, agente], queryFn: () => analisisLlamada(inicio, numero, agente), retry: false, staleTime: 5 * 60_000 });
  const [ver, setVer] = useState(false);
  const d = a.data;
  if (!d) return null;
  if (!d.resumen && !d.transcripcion)
    return (
      <p className="note" style={{ margin: 0 }}>
        3CX grabó esta llamada, pero no le hizo resumen ni transcripción. Para tenerlos aquí, actívalos en 3CX (transcripción con IA de las grabaciones y envío de
        «Recordings Data» por el Data Connector).
      </p>
    );
  // 3CX no documenta la escala: se lleva a 0..1 venga como -1..1, 0..1, 1..10 o 0..100.
  // 0 = 3CX no lo analizo (p. ej. «No meaningful conversation»): no se muestra.
  const v = d.sentimiento || null;
  const sent = v == null ? null : v < 0 ? (v + 1) / 2 : v <= 1 ? v : v <= 10 ? v / 10 : v / 100;
  const tono = sent == null ? undefined : sent >= 0.6 ? 'ok' : sent <= 0.4 ? 'bad' : 'warn';
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h3 className="lbl" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
        Resumen de 3CX
        {sent != null ? (
          <Pill tone={tono} dot>
            Sentimiento {Math.round(sent * 100)}%
          </Pill>
        ) : null}
      </h3>
      {d.resumen ? <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{d.resumen}</p> : null}
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {d.resumen && onUsarResumen ? (
          <button className="btn sm" type="button" onClick={() => onUsarResumen(d.resumen ?? '')}>
            Usar el resumen en las notas
          </button>
        ) : null}
        {d.transcripcion ? (
          <button className="btn sm ghost" type="button" onClick={() => setVer((v) => !v)}>
            {ver ? 'Ocultar transcripción' : 'Ver transcripción'}
          </button>
        ) : null}
      </span>
      {ver && d.transcripcion ? <p style={{ margin: 0, whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto', fontSize: 13.5 }}>{d.transcripcion}</p> : null}
    </section>
  );
}
